import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, Plane, FileCheck2, Radar, Boxes, ArrowRight } from "lucide-react";

const LANES = ["India → USA", "India → UK", "India → UAE", "India → Australia", "India → Canada", "India → Europe"];
const FEATURES = [
  { icon: Plane, text: "International shipping with multi-carrier options" },
  { icon: Radar, text: "End-to-end tracking across borders" },
  { icon: FileCheck2, text: "Documentation & customs workflow support" },
  { icon: Boxes, text: "Full shipment visibility from pickup to delivery" },
];

export default function InternationalSection() {
  return (
    <section className="relative overflow-hidden bg-[#071B3A] py-20 sm:py-24" data-testid="international-section">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#12C8F5]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#10B981]/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 px-4 py-1.5 text-xs font-bold tracking-wide text-[#10B981]">
            <Globe2 className="h-3.5 w-3.5" /> INTERNATIONAL SHIPPING
          </span>
          <h2 className="font-display mt-5 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">Take Your Business Global</h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">
            Ship from India to customers worldwide with automated forwarding, customs-ready documentation and live milestone tracking.
          </p>
          <ul className="mt-7 space-y-3.5">
            {FEATURES.map((f, i) => (
              <motion.li key={f.text} initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="flex items-center gap-3 text-sm text-slate-300">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#10B981]/30 bg-[#10B981]/10">
                  <f.icon className="h-4 w-4 text-[#10B981]" />
                </span>
                {f.text}
              </motion.li>
            ))}
          </ul>
          <Link to="/signup" data-testid="intl-cta-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-[#10B981] px-7 py-3.5 font-display text-sm font-bold text-[#030B18] transition-all hover:shadow-[0_10px_36px_rgba(16,185,129,0.45)]">
            START INTERNATIONAL SHIPPING <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
          className="grid gap-3 sm:grid-cols-2">
          {LANES.map((lane, i) => (
            <motion.div key={lane} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur transition-colors hover:border-[#10B981]/40"
              data-testid={`intl-lane-${lane.split("→ ")[1].toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-bold text-white">{lane}</span>
                <Plane className="h-4 w-4 text-slate-500 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#10B981]" />
              </div>
              <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/5">
                <motion.span initial={{ x: "-100%" }} whileInView={{ x: "0%" }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1, duration: 1 }}
                  className="block h-full w-full rounded-full bg-gradient-to-r from-[#FF9933] via-[#12C8F5] to-[#10B981]" />
              </div>
              <p className="mono mt-2 text-[9px] uppercase tracking-widest text-slate-500">Multi-carrier · Tracked</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
